import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyParentStudents, getStudentWeeklyPracticePlanSummary } from '../api/me';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { getSessionSummary, getSessions } from '../api/tutor';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function sortSessionsByRecency(a, b) {
  const left = Date.parse(a?.started_at || '') || 0;
  const right = Date.parse(b?.started_at || '') || 0;
  if (right !== left) {
    return right - left;
  }
  return Number(b?.id || 0) - Number(a?.id || 0);
}

function getLatestTutorSession(sessions) {
  if (!Array.isArray(sessions)) {
    return null;
  }
  const tutorSessions = sessions.filter((session) => {
    if (!session || typeof session !== 'object') {
      return false;
    }
    if (String(session.mode || '').trim() === 'TUTOR') {
      return true;
    }
    return session.exam_mode === false;
  });
  tutorSessions.sort(sortSessionsByRecency);
  return tutorSessions[0] || null;
}

function statusClassName(status) {
  if (status === 'COMPLETE') {
    return 'status-pill weekly-practice-plan-pill-complete';
  }
  if (status === 'IN_PROGRESS') {
    return 'status-pill weekly-practice-plan-pill-progress';
  }
  return 'status-pill weekly-practice-plan-pill-not-started';
}

function statusLabel(status) {
  if (status === 'COMPLETE') {
    return 'Complete';
  }
  if (status === 'IN_PROGRESS') {
    return 'In progress';
  }
  return 'Not started';
}

function itemCountSummary(item) {
  const count = Number(item?.count || 0);
  if (count <= 0) {
    return 'No current items flagged.';
  }
  if (item?.key === 'independent_practice') {
    return count === 1
      ? '1 area currently looks ready for more independent practice.'
      : `${count} areas currently look ready for more independent practice.`;
  }
  return count === 1
    ? '1 area currently needs attention this week.'
    : `${count} areas currently need attention this week.`;
}

function ParentStudentsPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const role = user?.role || null;
  const [students, setStudents] = useState([]);
  const [weeklyPlansByStudentId, setWeeklyPlansByStudentId] = useState({});
  const [studentProgress, setStudentProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      setErrorMessage('');
      try {
        const response = await getMyParentStudents();
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load students.'));
        }
        if (isMounted) {
          setStudents(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load students.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadStudents();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!students.length) {
      setWeeklyPlansByStudentId({});
      setStudentProgress({});
      return () => {
        isMounted = false;
      };
    }

    const loadStudentDetails = async () => {
      const weeklyPlanEntries = await Promise.all(
        students.map(async (student) => {
          try {
            const response = await getStudentWeeklyPracticePlanSummary(student.id);
            const data = await parseJsonSafe(response);
            if (!response.ok) {
              throw new Error(extractErrorMessage(data, 'Failed to load weekly practice plan.'));
            }
            return [student.id, { plan: data, error: '' }];
          } catch (error) {
            return [student.id, { plan: null, error: error.message || 'Failed to load weekly practice plan.' }];
          }
        })
      );

      const progressEntries = await Promise.all(
        students.map(async (student) => {
          const studentId = Number(student?.id || 0);
          if (!studentId) {
            return [studentId, { latestSessionId: null, summary: null, error: '' }];
          }

          try {
            const sessionsResponse = await getSessions({ studentId });
            const sessionsData = await parseJsonSafe(sessionsResponse);
            if (!sessionsResponse.ok) {
              throw new Error(extractErrorMessage(sessionsData, 'Failed to load sessions.'));
            }

            const latestTutorSession = getLatestTutorSession(
              Array.isArray(sessionsData) ? sessionsData : []
            );
            if (!latestTutorSession?.id) {
              return [studentId, { latestSessionId: null, summary: null, error: '' }];
            }

            const summaryResponse = await getSessionSummary(latestTutorSession.id);
            const summaryData = await parseJsonSafe(summaryResponse);
            if (!summaryResponse.ok) {
              throw new Error(extractErrorMessage(summaryData, 'Failed to load latest summary.'));
            }

            return [
              studentId,
              {
                latestSessionId: Number(latestTutorSession.id),
                summary: summaryData ?? null,
                error: '',
              },
            ];
          } catch (error) {
            return [
              studentId,
              {
                latestSessionId: null,
                summary: null,
                error: error.message || 'Failed to load progress snapshot.',
              },
            ];
          }
        })
      );

      if (isMounted) {
        setWeeklyPlansByStudentId(Object.fromEntries(weeklyPlanEntries));
        setStudentProgress(Object.fromEntries(progressEntries));
      }
    };

    loadStudentDetails();
    return () => {
      isMounted = false;
    };
  }, [students]);

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Parent workspace</p>
          <h1>My children</h1>
          <p>Read-only progress, weekly practice summaries, and safe session summary entry points for linked students.</p>
        </div>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Linked accounts</p>
            <h2>Linked students</h2>
          </div>
        </div>
        {isLoading ? <p>Loading students...</p> : null}
        {!isLoading ? (
          <ul className="data-list">
            {students.map((student) => {
              const displayName = student.display_name || student.student_display_name || `Student ${student.id}`;
              const weeklyPlanState = weeklyPlansByStudentId[student.id] || null;
              const progress = studentProgress[Number(student.id)] || {};
              const summary = progress.summary || null;
              const snapshot = summary?.parent_progress_snapshot || null;
              const latestSessionId = progress.latestSessionId || null;
              return (
                <li key={student.id} className="data-list-item">
                  <div className="list-item-header">
                    <div>
                      <strong>{displayName}</strong>
                      <p className="muted-text">Student id: {student.id}</p>
                    </div>
                    <span className="status-pill">Read only</span>
                  </div>
                  <p className="muted-text">Read-only access enabled.</p>
                  {snapshot ? (
                    <div className="stack-xs">
                      <p><strong>Current focus:</strong> {snapshot.current_focus}</p>
                      <p><strong>Current challenge:</strong> {snapshot.current_challenge}</p>
                      <p><strong>Next action:</strong> {snapshot.next_action}</p>
                    </div>
                  ) : null}
                  {!snapshot && !progress.error ? (
                    <p className="muted-text">No parent-safe progress snapshot yet.</p>
                  ) : null}
                  {weeklyPlanState?.plan ? (
                    <section className="weekly-practice-plan weekly-practice-plan-compact">
                      <div className="weekly-practice-plan-header">
                        <div>
                          <p className="section-title">Weekly practice plan summary</p>
                          <p>{weeklyPlanState.plan.headline}</p>
                        </div>
                        <span className={statusClassName(weeklyPlanState.plan.status)}>
                          {statusLabel(weeklyPlanState.plan.status)}
                        </span>
                      </div>
                      <p className="muted-text">{weeklyPlanState.plan.detail}</p>
                      <ul className="weekly-practice-plan-list">
                        {(Array.isArray(weeklyPlanState.plan.items) ? weeklyPlanState.plan.items : []).map((item) => (
                          <li key={item.key} className="weekly-practice-plan-item">
                            <div className="weekly-practice-plan-item-header">
                              <strong>{item.title}</strong>
                              <span className={statusClassName(item.status)}>{statusLabel(item.status)}</span>
                            </div>
                            <p className="muted-text">{itemCountSummary(item)}</p>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                  {weeklyPlanState?.error ? (
                    <p role="alert" className="notice notice-error">{weeklyPlanState.error}</p>
                  ) : null}
                  {progress.error ? (
                    <p role="alert" className="notice notice-error">{progress.error}</p>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                      latestSessionId
                        ? navigate(`/sessions/${latestSessionId}/summary`, {
                          state: {
                            sessionId: String(latestSessionId),
                            summary,
                          },
                        })
                        : navigate('/dashboard')
                    }
                    disabled={!latestSessionId}
                  >
                    {latestSessionId ? 'View latest summary' : 'No summary yet'}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
        {!isLoading && !errorMessage && students.length === 0 ? (
          <div className="notice notice-info">
            <p>No linked children yet.</p>
            <p>Ask an admin/tutor to link a student to this parent account.</p>
            {role === 'ADMIN' ? (
              <p>
                <Link to="/admin/users">Open Admin: Users</Link>
              </p>
            ) : null}
          </div>
        ) : null}
        {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
      </section>
    </div>
  );
}

export default ParentStudentsPage;
